import { ark } from "@ark-ui/react/factory"
import { useWindowSize } from "@hooks/use-window-size"
import { IconButton } from "@ui/IconButton"
import { type FC, useState, type ComponentProps } from "react"
import { MdMenu, MdMenuOpen } from "react-icons/md"
import { Flex, styled } from "styled-system/jsx"

const CollapsibleSidebarConfig = styled(ark.div, {
  base: {
    color: "text.muted",
    height: "viewport",
    zIndex: "[1]",
    backgroundColor: "surface",
    boxShadow:
      "[89px 44px 28px 0 rgba(70, 40, 83, 0.00), 57px 28px 25px 0 rgba(70, 40, 83, 0.01), 32px 16px 21px 0 rgba(70, 40, 83, 0.03), 14px 7px 16px 0 rgba(70, 40, 83, 0.04), 4px 2px 9px 0 rgba(70, 40, 83, 0.05)]",
    transition:
      "[box-shadow 200ms ease-out, transform 200ms ease-out, width 200ms ease-out, min-width 200ms ease-out]",
    borderImageSource:
      "linear-gradient(180deg, rgba(239,58,66,0.5) 0%, rgba(255,178,32,0.5) 49%, rgba(255,145,179,0.5) 100%)",
    borderImageSlice: 1,
    borderRight: true,
    left: "[0]",
    top: "[0]",
    bottom: "[0]",

    // 🔑 Make transform static; value comes from a CSS var.
    transform: "translateX(var(--sidebar-x, 0%))",

    "& .sidebar-link": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "[12px]",
      whiteSpace: "nowrap",
      paddingY: "sm",
      paddingX: "xs",
      borderRadius: "[6px]",
      textDecoration: "none",
      fontWeight: "[600]",
    },

    "& .sidebar-link:not([data-status=active]):hover": {
      transition: "[background-color 200ms ease-out, transform 200ms ease-out]",
      backgroundColor: "[#0C14360A]",
    },

    "& .sidebar-link[data-status=active]": {
      transition: "[background-color 200ms ease-out, transform 200ms ease-out]",
      backgroundColor: "[#CBD5E0]",
    },

    "& .sidebar-link-dot": {
      width: "[6px]",
      height: "[6px]",
      borderRadius: "full",
      backgroundColor: "[transparent]",
    },

    "& .sidebar-link[data-status=active] .sidebar-link-dot": {
      backgroundColor: "[#7E4797]",
      background:
        "[linear-gradient(90.71deg, #462853 0%, #623875 35%, #C79DDF 100%), radial-gradient(76.67% 153.33% at 130.67% 154.67%, rgba(255, 153, 3, 0.5) 0.19%, rgba(255, 153, 3, 0.25) 65%, rgba(255, 153, 3, 0) 100%), radial-gradient(52% 104% at -2% -4.67%, rgba(51, 196, 179, 0.75) 0%, rgba(51, 196, 179, 0) 100%), linear-gradient(0deg, rgba(98, 56, 117, 0.5), rgba(98, 56, 117, 0.5))]",

      marginRight: "sm",
    },
  },
  variants: {
    open: {
      visible: {
        "--sidebar-x": "[0%]",
        position: "relative",
      },
      hidden: {
        "--sidebar-x": "[-95%]",
        position: "fixed",
      },
    },
  },
  defaultVariants: {
    open: "visible",
  },
})
const CollapsibleSidebarItemConfig = styled(ark.div)

export type CollapsibleSidebarItemProps = ComponentProps<
  typeof CollapsibleSidebarItemConfig
>

const CollapsibleSidebarItem: FC<CollapsibleSidebarItemProps> = (props) => {
  const { ...rest } = props

  return (
    <CollapsibleSidebarItemConfig
      className="sidebar-link"
      {...rest}
    />
  )
}

const SIDEBAR_WIDTH = "[13.4rem]"
export const DESKTOP_WIDTH = 1280

export type CollapsibleSidebarProps = Omit<
  ComponentProps<typeof CollapsibleSidebarConfig>,
  "open"
>

const CollapsibleSidebarRoot: FC<CollapsibleSidebarProps> = (props) => {
  const { width = SIDEBAR_WIDTH, minWidth, children, ...rest } = props
  const { width: windowWidth } = useWindowSize()
  const isDesktop = windowWidth >= DESKTOP_WIDTH
  const [isDesktopOpen, setIsDesktopOpen] = useState(true)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const isSidebarOpen = isDesktop ? isDesktopOpen : isMobileOpen

  return (
    <CollapsibleSidebarConfig
      {...rest}
      width={width}
      minWidth={minWidth}
      open={isSidebarOpen ? "visible" : "hidden"}
    >
      <Flex
        width={width}
        minWidth={minWidth}
        height="full"
      >
        <IconButton
          height="md"
          minWidth="md"
          position="absolute"
          top="[6px]"
          right={isSidebarOpen ? "[-15px]" : "[-25px]"}
          color="var(--colors-gray-dark)"
          rounded="full"
          shadow
          background="var(--gradients-weak)"
          type="button"
          aria-expanded={isSidebarOpen}
          icon={isSidebarOpen ? MdMenuOpen : MdMenu}
          onClick={() => {
            if (isDesktop) {
              setIsDesktopOpen(!isDesktopOpen)
              return
            }

            setIsMobileOpen(!isMobileOpen)
          }}
        />
        <Flex
          paddingX="md"
          paddingTop="lg"
          paddingBottom="sm"
          width={width}
          minWidth={minWidth}
          flexShrink="0"
          aria-hidden={!isSidebarOpen}
          data-testid="sidebar-content"
        >
          {children}
        </Flex>
      </Flex>
    </CollapsibleSidebarConfig>
  )
}

export { CollapsibleSidebarRoot, CollapsibleSidebarItem }
